import * as THREE from 'three';
import { mmToMeshUnits } from './loadCortexMesh';

// The brain stem runs roughly 56 mm from the midbrain, through the pons, down to
// the foramen magnum where the medulla becomes the spinal cord — descending well
// below the inferior surface of the cerebral hemispheres. Dimensions are given
// in millimetres and converted at the cortical surface mesh's scale so the stem
// stays proportionate to the real cortex.
const STEM_HEIGHT = mmToMeshUnits(56);

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Radius profile along the brain stem, where t=0 is the inferior medulla and
 * t=1 is the midbrain. The pons is the widest point — it is the prominent
 * bulge on the anterior aspect of the stem.
 */
function stemRadius(t: number): number {
  const medulla = mmToMeshUnits(8) + mmToMeshUnits(3) * smoothstep(0, 0.35, t);
  const pons = mmToMeshUnits(6.5) * Math.exp(-(((t - 0.52) / 0.16) ** 2));
  const midbrain = mmToMeshUnits(2) * smoothstep(0.72, 1.0, t);
  return medulla + pons + midbrain;
}

/** The pons projects anteriorly, not just radially — hence a directional offset. */
function anteriorBulge(t: number): number {
  return mmToMeshUnits(7) * Math.exp(-(((t - 0.52) / 0.18) ** 2));
}

/**
 * Builds the brain stem with its three subdivisions — midbrain (superior),
 * pons (the anterior bulge), and medulla oblongata (tapering inferiorly).
 * Open-ended so the wireframe reads as a continuous tube joining the
 * cerebrum above and narrowing toward the spinal cord below.
 */
export function createBrainStemGeometry(): THREE.BufferGeometry {
  const cylinder = new THREE.CylinderGeometry(1, 1, STEM_HEIGHT, 28, 24, true);
  const positions = cylinder.attributes.position;
  const vertexCount = positions.count;
  const deformed = new Float32Array(vertexCount * 3);

  for (let i = 0; i < vertexCount; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);

    const t = (y + STEM_HEIGHT / 2) / STEM_HEIGHT;
    const angle = Math.atan2(z, x);
    const radius = stemRadius(t);

    deformed[i * 3] = Math.cos(angle) * radius;
    deformed[i * 3 + 1] = y;
    deformed[i * 3 + 2] = Math.sin(angle) * radius + anteriorBulge(t);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(deformed, 3));
  geometry.setIndex(cylinder.index);
  geometry.computeVertexNormals();
  cylinder.dispose();

  return geometry;
}
